import _ from 'underscore';
import Marionette from 'backbone.marionette';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import AlertMixin from '@/mixins/alert';
import CodeEditorView from '@/views/code_editor';
import CodeInfoView from '@/views/code_info';
import DevicesPaneView from '@/views/device_pane_layout';
import template from '@/../templates/code-layout.hbs';
import _resizable from 'jquery-ui/ui/widgets/resizable';

var SAVE_DRAFT_THROTTLE_MS = 10000;
var apiCh = Radio.channel('api');

var CodeLayout = Marionette.View.extend(AlertMixin).extend({
    template: Handlebars.compile(template),
    className: 'content code-layout content-container',
    ui: {
        editor_container: '.editor-container',
        vertical_resize: '.vertical-resize',
        horizontal_resize: '.horizontal-resize',
        show_devices_pane: '.show-devices-pane',
        hide_devices_pane: '.hide-devices-pane',
        alerts: '.alerts'
    },
    regions: {
        editor_toolbar: '.editor-toolbar-region',
        agent_editor: '.agent-editor',
        device_editor: '.device-editor',
        devices_pane: '.devices-pane-region',
        alerts: '@ui.alerts'
    },
    events: {
        'click @ui.show_devices_pane': 'show_devices_pane',
        'click @ui.hide_devices_pane': 'hide_devices_pane'
    },
    childViewEvents: function() {
        return {
            set_fullscreen: this.toggle_fullscreen,
            set_theme: this.set_theme,
            build: this.build
        };
    },
    /**
     * childEvent handlers
     *
     * The first argument to these childEvent handlers is always
     * the view which triggered the event
     */
    /**
     * childView event handler: Switch between fullscreen/not-fullscreen states
     */
    toggle_fullscreen: function(is_fullscreen) {
        if (is_fullscreen) {
            this.$el.addClass('fullscreen');
            this.options.settings.set('fullscreen', true);
        } else {
            this.$el.removeClass('fullscreen');
            this.options.settings.set('fullscreen', false);
        }
        // save fullscreen settings
        this.options.settings.save();
        // Determine if the resize handle is visible 
        // within the current viewport
        var horizontal_resize_rect 
            = this.ui.horizontal_resize[0].getBoundingClientRect();
        var hide_handle_rect 
            = this.ui.hide_devices_pane[0].getBoundingClientRect();

        var viewport_height 
            = window.innerHeight || document.documentElement.clientHeight;
        var viewport_width 
            = window.innerWidth || document.documentElement.clientWidth;
        var handle_is_visible = (
            horizontal_resize_rect.top >= 0 &&
            horizontal_resize_rect.left >= 0 &&
            horizontal_resize_rect.bottom <= (viewport_height) && 
            horizontal_resize_rect.right <= (viewport_width) 
        );
        if (!handle_is_visible) {
            var resize_height = (
                viewport_height 
                - (
                    hide_handle_rect.bottom - viewport_height
                        + hide_handle_rect.height
                        + horizontal_resize_rect.height
                        + 80  // 80 is a mgic number that seems to work
                )
            );
            this.ui.editor_container.resizable('resizeTo', {
                height: resize_height,
            });
        }
        this.resize_editors();
    },
    /** 
     * childView event handler: Set the theme for the editor
     */
    set_theme: function(theme) {
        if (theme === 'github') {
            this.$el.addClass('theme-light');
        } else {
            this.$el.removeClass('theme-light');
        }
    },
    /** 
     * childView event handler: on build, run
     */
    build: function() {
        this.getRegion('editor_toolbar')
            .currentView.getRegion('deployment')
            .currentView.trigger('select');
    },
    is_draft: function() {
        return this.model.type === 'draft';
    },
    onRender: function(){
        var theme = this.options.settings.get('theme');
        var is_fullscreen = this.options.settings.get('fullscreen');
        if (is_fullscreen) {
            this.toggle_fullscreen(is_fullscreen);
        }
        if (theme === 'github') {
            this.$el.addClass('theme-light');
        } else {
            this.$el.removeClass('theme-light');
        }
        this.show_editor_toolbar(this.options.devicegroup);
        this.show_editors();
        if (this.options.devicegroup) {
            this.initialize_devices_pane(this.options.devicegroup, this.model, this.options.device);
        }
        if (this.options.device) {
            this.set_active_device(this.options.device);
        }
    },
    enable_editable_deployment_state: function(deployment){
        var editor_toolbar = this.getRegion('editor_toolbar').currentView;
        editor_toolbar.enable_editable_deployment_state(deployment);

        // Set onFocus for editor to create a new draft and enable_draft_state
        this.editors_listen_for_draft();
    },
    enable_draft_state: function(draft){
        var editor_toolbar = this.getRegion('editor_toolbar').currentView;
        // Set side-nav to "draft" thing

        editor_toolbar.enable_draft_state(draft);
    },
    enable_readonly_deployment_state: function(deployment){
        var editor_toolbar = this.getRegion('editor_toolbar').currentView;
        // Set side-nav to "sha" thing

        // Show top top left "return to draft" link
        // Set top-left title text to "SHA: {{sha}" and bold post-text to either (current deployment) OR (past deployment)
        // Set top-left grey subtext to deployment_date
        // Hide all those other buttons
        // Show and enable edit button
        // Show and enable promote button
        editor_toolbar.enable_readonly_deployment_state(deployment);
    },
    editors_listen_for_draft: function(){
        _.forEach(this.editors, function(editor){
            // After a draft turns into a deployment, wait for special editable deployment code editors to get focus
            // Then, take the draft (made identical to the most recent deployment in code_info.js) and make it the model for the code editor
            this.listenToOnce(editor, 'focus', function(){
                this.enable_draft_state(this.model);
                this.stopListening(editor, 'keydown');
            });
            this.listenToOnce(editor, 'keydown', function(){
                this.enable_draft_state(this.model);
                this.stopListening(editor, 'focus');
            });
        }, this);
    },
    enable_return_to_draft: function(draft) {
        var editor_toolbar = this.getRegion('editor_toolbar').currentView;
        editor_toolbar.enable_return_to_draft(draft);
    },
    show_editor_toolbar: function(devicegroup) {
        var editor_toolbar = new CodeInfoView({
            model: this.model,
            devicegroup: devicegroup,
            device: this.options.device,
            settings: this.options.settings
        });
        this.showChildView('editor_toolbar', editor_toolbar);

        this.listenTo(editor_toolbar, 'run', this.initialize_devices_pane);
        this.listenTo(editor_toolbar, 'syntax_check', this.handle_syntax_check_results);
        this.listenTo(editor_toolbar, 'state:editable:deployment', this.enable_editable_deployment_state);
    },
    show_editors: function() {
        var agent_editor = new CodeEditorView({
            type: 'agent',
            model: this.model,
            read_only: !this.is_draft(),
            settings: this.options.settings,
            devicegroup: this.getOption('devicegroup')
        });
        var device_editor = new CodeEditorView({
            type: 'device',
            model: this.model,
            read_only: !this.is_draft(),
            settings: this.options.settings,
            devicegroup: this.getOption('devicegroup')
        });

        this.editors = [agent_editor, device_editor];

        this.showChildView('agent_editor', agent_editor);
        this.showChildView('device_editor', device_editor);
        this.enable_editor_resize();

        if (this.is_draft()) {
            this.start_draft_listeners();
        }
    },
    enable_editor_resize: function() {
        this.getRegion('agent_editor').$el.resizable({
            containment: 'parent',
            handles: {
                e: this.ui.vertical_resize
            },
            // Trigger internal editor resize after panes are resized
            resize: _.bind(this.resize_editors, this)
        });
    },
    enable_devices_pane_resize: function() {
        // Short-circuit if already initialized
        if (this.ui.editor_container.resizable('instance')) {
            this.ui.editor_container.resizable('enable');
            return;
        }

        this.ui.editor_container.resizable({
            containment: 'parent',
            handles: {
                s: this.ui.horizontal_resize
            },
            resize: _.bind(this.resize_editors, this),
            // Remove flex: 1 from editor container to allow jQuery UI resize
            start: _.bind(function() {
                this.ui.editor_container.css('flex', 'none');
            }, this)
        });
    },
    disable_devices_pane_resize: function() {
        this.ui.editor_container.resizable('disable');
    },
    initialize_devices_pane: function(devicegroup, code_model, device) {
        let self = this;
        // Don't re-show devicegroup if already displayed
        var devices_pane_view = this.getRegion('devices_pane').currentView;
        if (devices_pane_view && devices_pane_view.devicegroup === devicegroup) {
            return;
        }
        // Show the human type if the devicegroup is a pre_production
        // or production devicegroup
        var show_human_type = _([
            'pre_production_devicegroup',
            'factoryfixture_devicegroup',
            'pre_factoryfixture_devicegroup'])
        .contains(devicegroup.type);
        
        var devices_coll;
        var options = {};

        if (devicegroup.type === 'pre_factoryfixture_devicegroup') {
            devices_coll = devicegroup.production_target_and_factory_devices;
        } else {
            devices_coll = devicegroup.devices;
            options.data = { 'page[size]': 100 }; 
        }

        devices_coll.fetch(options);
        
        devices_pane_view = new DevicesPaneView({
            collection: devices_coll,
            devicegroup: devicegroup,
            device: device,
            code_model: code_model,
            childViewOptions: {
                className: 'device',
                tagName: 'li',
                show_human_type: show_human_type
            }
        });
        this.showChildView('devices_pane', devices_pane_view);

        this.ui.show_devices_pane.tooltip({
            delay: { show: 1000 }
        });
        this.ui.hide_devices_pane.tooltip({
            delay: { show: 1000 }
        });
        this.show_devices_pane();
        // after devices pane has been shown
        // code layout needs to be aware of some of the things it can do
        this.listenTo(devices_pane_view, 'run', this.initialize_devices_pane);
        this.listenTo(devices_pane_view, 'syntax_check', this.handle_syntax_check_results);
        this.listenTo(devices_pane_view, 'state:active:device', this.set_active_device);
        // code layout also listens to it's devicegroup for our change:devices event,
        // which should send along with it an array of devices ids
        this.listenTo(devicegroup, 'change:devices', function(device_ids){
            if ( !device_ids || !device_ids.length ) return;

            device_ids.forEach(function(id){
                let d = apiCh.request('device', id);
                let devices_pane_collection = self.getChildView('devices_pane').collection;
                d.fetch().then(function(){
                    if ( d.get('devicegroup') && d.get('devicegroup').id === devicegroup.id ) {
                        devices_pane_collection.add( d, { merge: true });
                    } else {
                        devices_pane_collection.remove(d);
                    }
                }, function(err){
                    console.warn(err);
                });
            }); 
        }.bind(this));
    },
    set_active_device: function(device) {
        this.active_device = device;
        this.getRegion('editor_toolbar').currentView.device = this.active_device;
    },
    show_devices_pane: function() {
        // Restore saved height if it exists
        if (this.editor_height) {
            var container_height = this.$el.outerHeight();
            var new_editor_height = Math.min(this.editor_height, container_height);
            this.ui.editor_container.outerHeight(new_editor_height);
            this.ui.editor_container.css('flex', 'none');
        }

        this.getRegion('devices_pane').$el.removeClass('hidden');
        this.ui.show_devices_pane.hide();
        this.ui.hide_devices_pane.show();

        this.enable_devices_pane_resize();
        this.resize_editors();
    },
    hide_devices_pane: function() {
        // Save current height so we can restore it later
        this.editor_height = this.ui.editor_container.outerHeight();

        // Remove jQuery UI Resizable height override
        this.ui.editor_container.css('height', '');

        this.ui.editor_container.css('flex', '1');
        this.getRegion('devices_pane').$el.addClass('hidden');
        this.ui.hide_devices_pane.hide();
        this.ui.show_devices_pane.show();

        this.disable_devices_pane_resize();
        this.resize_editors();
    },
    save_draft: function() {
        this.model.save().then(function(){ this.model.changed = {}; }.bind(this));
    },
    start_draft_listeners: function() {
        this.throttled_save = _.debounce(this.save_draft, SAVE_DRAFT_THROTTLE_MS);
        // Occasionally save code drafts to the server
        this.listenTo(this.model, 'change:agent_code', this.throttled_save);
        this.listenTo(this.model, 'change:device_code', this.throttled_save);
        // when we have draft listeners, and the draft is built, we need
        // to cancel the current debounced throttled save to prevent a
        // second submission
        var editor_toolbar = this.getChildView('editor_toolbar');
        this.listenTo(editor_toolbar, 'build', this.throttled_save.cancel);
    },
    handle_syntax_check_results: function(results) {
        var devices_pane_view = this.getRegion('devices_pane').currentView;
        var agent_results = results[0];
        var device_results = results[1];

        this.editors[0].handle_syntax_check_results(agent_results);
        this.editors[1].handle_syntax_check_results(device_results);

        // Show syntax check failure messages in the log pane if it exists
        if (agent_results.length + device_results.length === 0) return;
        devices_pane_view.handle_syntax_check_results(results);
    },
    resize_editors: function() {
        _.each(this.editors, function(editor) {
            editor.resize();
        });
    },
    onDestroy: function() {
        if (this.is_draft() && _.keys(this.model.changed).length > 0) {
            this.throttled_save.cancel();
            this.save_draft();
        }
    }
});

export default CodeLayout;

